import { ILeaf, INode } from 'shared/types/tree'
import { isGDriveFileList, GDriveFile } from 'infrastructure/googleDrive'
import {
  fileLocationInfo,
  filesMappedToLessonNodeAtom,
  isUpdatingAtom,
  tableConfigMap,
  tableUpdateConfig,
  tableUpdateConfigAtom,
  treeIdMapAtom,
  workingTreeAtom,
} from './atoms'
import { nodesOfTypeWithContent } from '../../utility/TreeHelpers'
import { NodeType } from 'shared/types/ingestion'
import { byStringFieldSort } from '../../Helpers'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { useCallback } from 'react'
import { deleteLeaf, deleteNode } from '../TreeActions'
import { getTableKey, useNoteNodeContentForTable } from './tableIngest'
import { parseHtmlString, doInBatches } from 'shared/utils'
import { useSetAtom } from 'infrastructure/state/jotai.wrapper'
import { appendNodeToTree, retrieveIds } from '../TreeRequests'
import { useGetDoc } from '../googleApis/hooks'

type updateData = {
  key: string
  required: boolean
  updateFunc: (noteContentMap: Record<string, string>) => Promise<unknown>
}

function getAllLessonFoldersForGradeFolder(gradeFolder: GDriveFile) {
  const unitIdRegex = /^\d+(\.\d+)?$/
  const theFolder = gradeFolder.files
  if (!theFolder || !isGDriveFileList(theFolder)) return []
  return theFolder.files.reduce<GDriveFile[]>((acc, file) => {
    const [lessonId] = file.name.split(' ')
    if (!unitIdRegex.test(lessonId)) return acc
    return [...acc, file]
  }, [])
}

// This drills down into the grade folder and finds the lessons folder
// returns all files mapped to the unit id
export function getFilesByUnitIdForGradeFolder(gradeFolder: GDriveFile) {
  const lessonFolders = getAllLessonFoldersForGradeFolder(gradeFolder)
  return lessonFolders.reduce<Record<string, GDriveFile[]>>((acc, folder) => {
    const [lessonId] = folder.name.split(' ')
    if (!isGDriveFileList(folder.files)) return acc
    const lessonFolder = folder.files.files.find(
      ({ name }) => name === 'Lessons',
    )
    if (!lessonFolder || !isGDriveFileList(lessonFolder.files)) return acc
    return {
      ...acc,
      [lessonId]: lessonFolder.files.files,
    }
  }, {})
}

export function getFilesMappedToLessonNode(
  tree: INode,
  filesByUnit: Record<string, GDriveFile[]>,
) {
  const lessonNumRegex = /L(\d+)/
  return Object.entries(filesByUnit)
    .reduce<fileLocationInfo[]>((acc, [unitCode, fileList]) => {
      const unitNum = Number(unitCode.split('.').pop())
      if (!unitNum)
        return {
          ...acc,
          ...fileList.map((file) => ({ file })),
        }
      fileList.forEach((file) => {
        const lessonNum = Number(file.name.match(lessonNumRegex)?.[1])
        if (!lessonNum) {
          acc.push({ file, unitNum })
        }
        const nodeId = nodeForFileLocation(tree, { unitNum, lessonNum })
        acc.push({ file, unitNum, lessonNum, nodeId })
      })
      return acc
    }, [])
    .sort(byStringFieldSort('unitNum', 'lessonNum'))
}

function nodeForFileLocation(
  tree: INode,
  { unitNum, lessonNum }: { unitNum?: number; lessonNum?: number },
) {
  if (!unitNum || !lessonNum) return
  const unitNodeCheck = ({ title }: INode) => title.includes(`.${unitNum}`)
  const lessonNodeCheck = ({ title }: INode) =>
    title.includes(`Lesson ${lessonNum}:`)
  const [unit] = nodesOfTypeWithContent({
    tree,
    types: [NodeType.Unit],
    extraCheck: unitNodeCheck,
  })
  if (!unit) return
  const [lesson] = nodesOfTypeWithContent({
    tree: unit,
    types: [NodeType.LessonPlan],
    extraCheck: lessonNodeCheck,
  })
  return lesson?.id
}

function existingNodesForConfig(
  { nodes }: INode,
  { title }: tableUpdateConfig,
) {
  return nodes.filter(({ type, title: nodeTitle }) => {
    return (
      type === NodeType.Note &&
      nodeTitle.toLowerCase().includes(title.toLowerCase())
    )
  })
}

function nodeDeleteUpdates(parentNode: INode, updatesConfig: tableConfigMap) {
  const { tree_id, version } = parentNode
  return Object.values(updatesConfig).reduce<updateData[]>((acc, config) => {
    const { key } = config
    return [
      ...acc,
      ...existingNodesForConfig(parentNode, config).map(({ id }, index) => {
        return {
          key: `ND DLT: ${key}`,
          required: !!index, // node delete required if there's duplicates
          updateFunc: () => {
            return deleteNode({
              treeId: tree_id,
              versionId: version,
              nodeId: id,
            })
          },
        }
      }),
    ]
  }, [])
}

function getAddUpdate(
  tableKey: string,
  parentNode: INode,
  nextNode: INode | undefined,
  config: tableUpdateConfig,
) {
  const { key, title } = config
  return {
    key: `ND ADD: ${key}`,
    required: !existingNodesForConfig(parentNode, config).length,
    updateFunc: (noteContentMap: Record<string, string>) => {
      const noteNode = {
        title,
        type: NodeType.Note,
        body: {
          value: noteContentMap[tableKey],
        },
      }
      return appendNodeToTree({
        ...retrieveIds(parentNode),
        ...noteNode,
        next_sibling_node_id: nextNode?.id,
      }).then(() => {})
    },
  }
}

function nodeAddUpdates(parentNode: INode, updatesConfig: tableConfigMap) {
  const nextNode = parentNode.nodes.find(
    ({ type, body }) =>
      type === NodeType.NodeList && body?.type === NodeType.Activity,
  )
  return Object.entries(updatesConfig).map<updateData>(
    ([tableKey, configData]) => {
      return getAddUpdate(tableKey, parentNode, nextNode, configData)
    },
  )
}

function attachmentsUpdates(node: INode, updatesConfig: tableConfigMap) {
  const attachmentsNode = node.nodes.find(
    ({ type }) => type === NodeType.AttachmentsList,
  )
  if (!attachmentsNode) return []

  const hasFile = (idKey: string) => {
    return ({
      body: {
        file: { id },
      },
    }: ILeaf) => String(id).toLowerCase().includes(idKey.toLowerCase())
  }

  return Object.values(updatesConfig).reduce<updateData[]>(
    (acc, { key, attachmentsKey }) => {
      const attachmentsLeaf =
        attachmentsKey && attachmentsNode.leaves?.find(hasFile(attachmentsKey))
      if (!attachmentsLeaf) return acc
      return [
        ...acc,
        {
          key: `ATT DEL: ${key}`,
          required: true, // deleting attachments is always required
          updateFunc: () =>
            deleteLeaf({
              treeId: node.tree_id,
              versionId: node.version,
              leafId: attachmentsLeaf.id,
            }),
        },
      ]
    },
    [],
  )
}

export function getUpdatesData(
  node: INode,
  updatesConfig: tableConfigMap,
  onlyRequired: boolean = false,
) {
  const updates = [
    ...attachmentsUpdates(node, updatesConfig),
    ...nodeDeleteUpdates(node, updatesConfig),
    ...nodeAddUpdates(node, updatesConfig),
  ]
  if (!onlyRequired) return updates
  return updates.filter(({ required }) => required)
}

export function useDoUpdates(onlyRequired = false) {
  const { resetTree } = useAtomValue(workingTreeAtom)
  const config = useAtomValue(tableUpdateConfigAtom)
  const idMap = useAtomValue(treeIdMapAtom)
  const setUpdating = useSetAtom(isUpdatingAtom)
  const fileParser = useTableNodeContentForFile()

  return useCallback(
    async (...updateData: fileLocationInfo[]) => {
      const doUpdate = async ({ nodeId, file }: fileLocationInfo) => {
        if (!nodeId) return
        const noteContentMap = await fileParser(file)
        if (!noteContentMap) return
        const node = idMap?.[nodeId]
        if (!node) return
        const updates = getUpdatesData(node.node, config, onlyRequired)
        return updates.reduce<Promise<unknown>>(async (acc, { updateFunc }) => {
          await acc
          return updateFunc(noteContentMap).catch((e) => console.log('ERR', e))
        }, Promise.resolve())
      }

      setUpdating(true)
      await doInBatches(updateData, doUpdate)
      await resetTree?.()
      setUpdating(false)
    },
    [resetTree, config, idMap, setUpdating, fileParser],
  )
}

function parseTablesHtmlString(
  htmlString: string,
  tableConfig: tableConfigMap,
) {
  const asHtml = parseHtmlString(htmlString)
  const tables = Array.from(asHtml.getElementsByTagName('table'))
  return tables.reduce<Record<string, HTMLTableElement[]>>((acc, table) => {
    const tableKey = getTableKey(table)
    if (!tableKey || !(tableKey in tableConfig)) return acc
    if (!(tableKey in acc)) {
      acc[tableKey] = []
    }
    acc[tableKey].push(table)
    return acc
  }, {})
}

function useTableNodeContentForFile() {
  const noteNodeParser = useNoteNodeContentForTable()
  const tableConfig = useAtomValue(tableUpdateConfigAtom)
  const { canGetDoc, getDoc } = useGetDoc()
  return useCallback(
    async (file: GDriveFile) => {
      if (!canGetDoc) return

      const docContent = await getDoc(file.id)
      // This is html tables for table keys, we want to convert these tables to node content
      const tableMap = parseTablesHtmlString(docContent, tableConfig)
      return Object.entries(tableMap).reduce<Record<string, string>>(
        (acc, [key, [table]]) => {
          return {
            ...acc,
            [key]: noteNodeParser(table),
          }
        },
        {},
      )
    },
    [getDoc, canGetDoc, noteNodeParser, tableConfig],
  )
}

export function useDoAllUpdates() {
  const updater = useDoUpdates()
  const filesByLessonId = useAtomValue(filesMappedToLessonNodeAtom)
  return useCallback(async () => {
    return updater(...filesByLessonId)
  }, [filesByLessonId, updater])
}

export function useOnlyRequiredUpdates() {
  const updater = useDoUpdates(true)
  const filesByLessonId = useAtomValue(filesMappedToLessonNodeAtom)
  return useCallback(async () => {
    return updater(...filesByLessonId)
  }, [filesByLessonId, updater])
}
